import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  FormControlLabel,
  Switch,
  Collapse,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Refresh,
  Delete as DeleteIcon,
  ExpandMore,
  ExpandLess,
  Warning,
  NewspaperOutlined,
  ShowChart,
  CompareArrows,
  ArrowUpward,
  ArrowDownward,
  Remove,
} from '@mui/icons-material';
import { apiService, Recommendation, ComprehensiveScore } from '../services/api';
import { useTheme } from '@mui/material/styles';

// ── Score bar ─────────────────────────────────────────────────────────────────
const ScoreBar: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  color?: string;
}> = ({ label, value, icon, color }) => {
  const theme = useTheme();
  const barColor = color || (
    value >= 65 ? theme.palette.success.main
    : value >= 45 ? theme.palette.warning.main
    : theme.palette.error.main
  );
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ color: barColor, display: 'flex', alignItems: 'center' }}>{icon}</Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
        <Typography variant="caption" fontWeight={700} sx={{ color: barColor }}>
          {value.toFixed(0)}/100
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: theme.palette.action.hover,
          '& .MuiLinearProgress-bar': { backgroundColor: barColor, borderRadius: 3 },
        }}
      />
    </Box>
  );
};

// ── Divergence badge ──────────────────────────────────────────────────────────
const DIVERGENCE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  bearish_divergence: { bg: '#fff3e0', text: '#e65100', border: '#ff9800' },
  bullish_divergence: { bg: '#e8f5e9', text: '#1b5e20', border: '#4caf50' },
  aligned_bullish:    { bg: '#e3f2fd', text: '#0d47a1', border: '#2196f3' },
  aligned_bearish:    { bg: '#fce4ec', text: '#880e4f', border: '#e91e63' },
  neutral:            { bg: '#f5f5f5', text: '#616161', border: '#bdbdbd' },
};

const DIVERGENCE_LABELS: Record<string, string> = {
  bearish_divergence: '⚠ Bearish Divergence',
  bullish_divergence: '↑ Bullish Divergence',
  aligned_bullish:    '✓ Aligned Bullish',
  aligned_bearish:    '✓ Aligned Bearish',
  neutral:            '— Neutral',
};

// ── Comprehensive insight panel (shown below each expanded row) ───────────────
const ComprehensiveInsightPanel: React.FC<{
  rec: Recommendation;
}> = ({ rec }) => {
  const theme = useTheme();
  const [data, setData] = useState<ComprehensiveScore | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiService
      .getComprehensiveScore(
        rec.symbol,
        rec.confidence_score * 100,
        'BUY',
        72,
      )
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load insight'))
      .finally(() => setLoading(false));
  }, [rec.symbol, rec.confidence_score]);

  if (loading) {
    return (
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading comprehensive score…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: 3, py: 1 }}>
        <Typography variant="caption" color="text.secondary">Insight unavailable: {error}</Typography>
      </Box>
    );
  }

  if (!data) return null;

  const divColors = DIVERGENCE_COLORS[data.divergence.divergence_type] || DIVERGENCE_COLORS.neutral;
  const priceSign = data.price_trend.change_3d_pct >= 0 ? '+' : '';
  const priceColor =
    data.price_trend.trend === 'up' ? theme.palette.success.main
    : data.price_trend.trend === 'down' ? theme.palette.error.main
    : theme.palette.text.secondary;

  const PriceTrendIcon =
    data.price_trend.trend === 'up' ? ArrowUpward
    : data.price_trend.trend === 'down' ? ArrowDownward
    : Remove;

  const newsColor =
    data.news.direction === 'bullish' ? theme.palette.success.main
    : data.news.direction === 'bearish' ? theme.palette.error.main
    : theme.palette.text.secondary;

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.03)'
          : 'rgba(0,0,0,0.02)',
        borderTop: `1px solid ${theme.palette.divider}`,
        px: 3,
        py: 2,
      }}
    >
      <Grid container spacing={3} alignItems="flex-start">

        {/* ── Comprehensive score ── */}
        <Grid item xs={12} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Comprehensive Score
            </Typography>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={data.comprehensive_score}
                size={72}
                thickness={5}
                sx={{
                  color:
                    data.comprehensive_score >= 65 ? theme.palette.success.main
                    : data.comprehensive_score >= 45 ? theme.palette.warning.main
                    : theme.palette.error.main,
                }}
              />
              <Box
                sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                }}
              >
                <Typography variant="body2" fontWeight={700} lineHeight={1}>
                  {data.comprehensive_score.toFixed(0)}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1}>/ 100</Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={data.recommendation}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  backgroundColor:
                    data.recommendation === 'STRONG_BUY' ? theme.palette.success.main + '20'
                    : data.recommendation === 'BUY' ? theme.palette.success.light + '30'
                    : data.recommendation === 'HOLD' ? theme.palette.warning.main + '20'
                    : theme.palette.error.main + '20',
                  color:
                    data.recommendation === 'STRONG_BUY' || data.recommendation === 'BUY'
                      ? theme.palette.success.main
                      : data.recommendation === 'HOLD'
                      ? theme.palette.warning.main
                      : theme.palette.error.main,
                }}
              />
              {data.recommendation !== data.original_recommendation && (
                <Typography variant="caption" display="block" color="text.secondary" mt={0.3}>
                  was {data.original_recommendation}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        {/* ── Score bars ── */}
        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
            Score Breakdown
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <ScoreBar
              label="Technical / Attractiveness"
              value={data.attractiveness_score}
              icon={<ShowChart sx={{ fontSize: 14 }} />}
            />
            <ScoreBar
              label="News Sentiment"
              value={data.news_score}
              icon={<NewspaperOutlined sx={{ fontSize: 14 }} />}
            />
            <ScoreBar
              label="Signal Alignment"
              value={data.alignment_score}
              icon={<CompareArrows sx={{ fontSize: 14 }} />}
            />
          </Box>
        </Grid>

        {/* ── News + price ── */}
        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
            Market Signals
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* News */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">News (72h)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: newsColor, textTransform: 'capitalize' }}>
                  {data.news.direction}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({data.news.signal_count} sig / {data.news.article_count} art)
                </Typography>
              </Box>
            </Box>
            {/* Price 3d */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Price (3d)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: priceColor }}>
                <PriceTrendIcon sx={{ fontSize: 13 }} />
                <Typography variant="caption" fontWeight={700}>
                  {priceSign}{data.price_trend.change_3d_pct.toFixed(1)}%
                </Typography>
              </Box>
            </Box>
            {/* Price 1d */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Price (1d)</Typography>
              <Typography variant="caption" fontWeight={700}
                sx={{ color: data.price_trend.change_1d_pct >= 0 ? theme.palette.success.main : theme.palette.error.main }}>
                {data.price_trend.change_1d_pct >= 0 ? '+' : ''}{data.price_trend.change_1d_pct.toFixed(1)}%
              </Typography>
            </Box>
            {data.news.dominant_category && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Category</Typography>
                <Chip label={data.news.dominant_category} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Confidence</Typography>
              <Typography variant="caption" fontWeight={600}>
                {(data.news.avg_confidence * 100).toFixed(0)}%
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* ── Divergence + insights ── */}
        <Grid item xs={12} sm={3}>
          {/* Divergence badge */}
          <Box
            sx={{
              backgroundColor: divColors.bg,
              border: `1px solid ${divColors.border}`,
              borderRadius: 2,
              px: 1.5,
              py: 1,
              mb: 1.5,
            }}
          >
            <Typography variant="caption" fontWeight={700} sx={{ color: divColors.text, display: 'block' }}>
              {DIVERGENCE_LABELS[data.divergence.divergence_type]}
            </Typography>
            {data.divergence.caution_level === 'high' && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
                <Warning sx={{ fontSize: 13, color: divColors.text, mt: 0.1, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: divColors.text, lineHeight: 1.4 }}>
                  {data.divergence.interpretation}
                </Typography>
              </Box>
            )}
            {data.divergence.caution_level !== 'high' && (
              <Typography variant="caption" sx={{ color: divColors.text }}>
                {data.divergence.interpretation}
              </Typography>
            )}
          </Box>

          {/* Key insights */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {data.key_insights.map((insight, i) => (
              <Typography key={i} variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                • {insight}
              </Typography>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// ── Main Recommendations page ─────────────────────────────────────────────────
const Recommendations: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [scanSymbols, setScanSymbols] = useState('');
  const [scanning, setScanning] = useState(false);
  const [availableOnly, setAvailableOnly] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [sortField, setSortField] = useState<'confidence_score' | 'expected_return_pct' | 'risk_reward_ratio'>('confidence_score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [stockMeta, setStockMeta] = useState<Record<string, { name: string | null; sector: string | null }>>({});

  const [approvalDialog, setApprovalDialog] = useState<{
    open: boolean;
    recommendation: Recommendation | null;
  }>({ open: false, recommendation: null });
  const [approvalQuantity, setApprovalQuantity] = useState('100');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approving, setApproving] = useState(false);

  const [valuation, setValuation] = useState<any>(null);
  const [valuationLoading, setValuationLoading] = useState(true);

  const handleSort = (field: typeof sortField) => {
    if (field === sortField) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedRecs = useMemo(() => {
    return [...recommendations].sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [recommendations, sortField, sortDir]);

  const fetchRecommendations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.listRecommendations(undefined, availableOnly, 50);
      setRecommendations(data.opportunities || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch recommendations');
    } finally {
      setLoading(false);
    }
  }, [availableOnly]);

  useEffect(() => { fetchRecommendations(); }, [fetchRecommendations]);

  useEffect(() => {
    apiService.getStockMeta().then(setStockMeta).catch(() => {});
  }, []);

  useEffect(() => {
    setValuationLoading(true);
    apiService.getMarketValuation()
      .then(setValuation)
      .catch(() => {})
      .finally(() => setValuationLoading(false));
  }, []);

  // After recommendations load, fetch company name+sector for any symbol that
  // doesn't already have it in strategy_data (i.e. older DB records).
  useEffect(() => {
    if (!recommendations.length) return;
    const missing = recommendations
      .filter(r => !r.strategy_data?.company_name)
      .map(r => r.symbol)
      .filter((s, i, arr) => arr.indexOf(s) === i); // dedupe
    if (!missing.length) return;
    apiService.getTickerInfo(missing).then(info => {
      setStockMeta(prev => {
        const next = { ...prev };
        for (const [sym, data] of Object.entries(info)) {
          if (!next[sym] || !next[sym].name) {
            next[sym] = { name: data.name, sector: data.sector };
          }
        }
        return next;
      });
    }).catch(() => {});
  }, [recommendations]);

  const handleScan = async () => {
    if (!scanSymbols.trim()) return;
    setScanning(true);
    setError(null);
    try {
      const symbols = scanSymbols.split(',').map(s => s.trim().toUpperCase());
      const result = await apiService.scanForRecommendations(symbols);
      setRecommendations(result.opportunities || []);
      setScanSymbols('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to scan symbols');
    } finally {
      setScanning(false);
    }
  };

  const handleScanAll = async () => {
    setScanning(true);
    setError(null);
    try {
      const result = await apiService.scanAllStocks(undefined, 0.45, 512, false);
      setRecommendations(result.opportunities || []);
      if (result.opportunities_found === 0) {
        setError(`Scanned ${result.total_scanned} stocks but found no trading opportunities.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to scan stocks.');
    } finally {
      setScanning(false);
    }
  };

  const handleApprove = (rec: Recommendation) => {
    setApprovalDialog({ open: true, recommendation: rec });
    setApprovalQuantity('100');
    setApprovalNotes('');
  };

  const handleConfirmApproval = async () => {
    if (!approvalDialog.recommendation) return;
    setApproving(true);
    try {
      await apiService.approveRecommendation(
        approvalDialog.recommendation.id,
        parseInt(approvalQuantity),
        approvalNotes || undefined
      );
      setApprovalDialog({ open: false, recommendation: null });
      fetchRecommendations();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to approve recommendation');
    } finally {
      setApproving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this recommendation?')) return;
    try {
      await apiService.deleteRecommendation(id);
      fetchRecommendations();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete recommendation');
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const getDirectionColor = (direction: string) =>
    direction === 'long' ? theme.palette.success.main : theme.palette.error.main;

  const getConfidenceColor = (confidence: number) =>
    confidence >= 0.8 ? theme.palette.success.main
    : confidence >= 0.6 ? theme.palette.warning.main
    : theme.palette.error.main;

  const severityColor = (s: string | null) => {
    if (s === 'success') return theme.palette.success.main;
    if (s === 'warning') return '#ff9800';
    if (s === 'error') return theme.palette.error.main;
    return theme.palette.info.main;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Trading Recommendations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        AI-generated trading opportunities — long and short — across 512 NASDAQ stocks. Click any row to see the comprehensive signal breakdown.
      </Typography>

      {/* Market Valuation Bar */}
      <Card sx={{ mb: 3, background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
        <CardContent sx={{ py: '12px !important', px: 2 }}>
          {valuationLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Loading market valuation indicators…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5, whiteSpace: 'nowrap' }}>
                Market Valuation:
              </Typography>

              {/* US Buffett Indicator */}
              {valuation?.us_buffett?.value != null && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" display="block" fontWeight={700}>US Buffett Indicator</Typography>
                      <Typography variant="caption" display="block">{valuation.us_buffett.description}</Typography>
                      <Typography variant="caption" display="block">Market Cap: ~${valuation.us_buffett.market_cap_trillions}T</Typography>
                      <Typography variant="caption" display="block">US GDP: ~${valuation.us_buffett.gdp_trillions}T</Typography>
                      <Typography variant="caption" display="block">Historical avg: ~{valuation.us_buffett.historical_mean_pct}%</Typography>
                    </Box>
                  }
                  arrow
                >
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1.5, py: 0.5, borderRadius: 2,
                    border: `1px solid ${severityColor(valuation.us_buffett.severity)}40`,
                    backgroundColor: `${severityColor(valuation.us_buffett.severity)}10`,
                    cursor: 'default',
                  }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>🇺🇸 Buffett</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: severityColor(valuation.us_buffett.severity) }}>
                      {valuation.us_buffett.value}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: severityColor(valuation.us_buffett.severity) }}>
                      · {valuation.us_buffett.label}
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              {/* Global Buffett Indicator */}
              {valuation?.global_buffett?.value != null && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" display="block" fontWeight={700}>Global Buffett Indicator</Typography>
                      <Typography variant="caption" display="block">{valuation.global_buffett.description}</Typography>
                      <Typography variant="caption" display="block">Adjusts for US corps' international revenue</Typography>
                      <Typography variant="caption" display="block">World GDP: ~${valuation.global_buffett.world_gdp_trillions}T ({valuation.global_buffett.world_gdp_year})</Typography>
                      <Typography variant="caption" display="block">Historical avg: ~{valuation.global_buffett.historical_mean_pct}%</Typography>
                    </Box>
                  }
                  arrow
                >
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1.5, py: 0.5, borderRadius: 2,
                    border: `1px solid ${severityColor(valuation.global_buffett.severity)}40`,
                    backgroundColor: `${severityColor(valuation.global_buffett.severity)}10`,
                    cursor: 'default',
                  }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>🌍 Global</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: severityColor(valuation.global_buffett.severity) }}>
                      {valuation.global_buffett.value}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: severityColor(valuation.global_buffett.severity) }}>
                      · {valuation.global_buffett.label}
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              {/* Shiller CAPE */}
              {valuation?.shiller_cape?.value != null && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" display="block" fontWeight={700}>Shiller CAPE Ratio</Typography>
                      <Typography variant="caption" display="block">{valuation.shiller_cape.description}</Typography>
                      <Typography variant="caption" display="block">10-yr inflation-adjusted earnings average</Typography>
                      <Typography variant="caption" display="block">Historical mean: {valuation.shiller_cape.historical_mean}×</Typography>
                      {valuation.shiller_cape.data_date && (
                        <Typography variant="caption" display="block" sx={{ color: 'warning.light', mt: 0.5 }}>
                          Data as of {valuation.shiller_cape.data_date} (Shiller/Yale)
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                >
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1.5, py: 0.5, borderRadius: 2,
                    border: `1px solid ${severityColor(valuation.shiller_cape.severity)}40`,
                    backgroundColor: `${severityColor(valuation.shiller_cape.severity)}10`,
                    cursor: 'default',
                  }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>CAPE</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: severityColor(valuation.shiller_cape.severity) }}>
                      {valuation.shiller_cape.value}×
                    </Typography>
                    <Typography variant="caption" sx={{ color: severityColor(valuation.shiller_cape.severity) }}>
                      · {valuation.shiller_cape.label}
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              {/* S&P 500 Trailing P/E */}
              {valuation?.sp500_trailing_pe?.value != null && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" display="block" fontWeight={700}>S&P 500 Trailing P/E</Typography>
                      <Typography variant="caption" display="block">{valuation.sp500_trailing_pe.description}</Typography>
                      <Typography variant="caption" display="block">Historical mean: {valuation.sp500_trailing_pe.historical_mean}×</Typography>
                      <Typography variant="caption" display="block">S&P 500: ${valuation.sp500_trailing_pe.sp500_price?.toLocaleString()}</Typography>
                    </Box>
                  }
                  arrow
                >
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1.5, py: 0.5, borderRadius: 2,
                    border: `1px solid ${severityColor(valuation.sp500_trailing_pe.severity)}40`,
                    backgroundColor: `${severityColor(valuation.sp500_trailing_pe.severity)}10`,
                    cursor: 'default',
                  }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>P/E</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: severityColor(valuation.sp500_trailing_pe.severity) }}>
                      {valuation.sp500_trailing_pe.value}×
                    </Typography>
                    <Typography variant="caption" sx={{ color: severityColor(valuation.sp500_trailing_pe.severity) }}>
                      · {valuation.sp500_trailing_pe.label}
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              {/* VIX */}
              {valuation?.vix?.value != null && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" display="block" fontWeight={700}>VIX — Fear Index</Typography>
                      <Typography variant="caption" display="block">{valuation.vix.description}</Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {'<'}15: Low Fear · 15–25: Normal · 25–35: Elevated · {'>'}35: Extreme
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1.5, py: 0.5, borderRadius: 2,
                    border: `1px solid ${severityColor(valuation.vix.severity)}40`,
                    backgroundColor: `${severityColor(valuation.vix.severity)}10`,
                    cursor: 'default',
                  }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>VIX</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: severityColor(valuation.vix.severity) }}>
                      {valuation.vix.value.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: severityColor(valuation.vix.severity) }}>
                      · {valuation.vix.label}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Scan Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                label="Scan Symbols (comma-separated)"
                value={scanSymbols}
                onChange={(e) => setScanSymbols(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleScan()}
                placeholder="AAPL, MSFT, GOOGL"
                helperText="Enter stock symbols separated by commas"
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Button fullWidth variant="contained" onClick={handleScan}
                disabled={scanning || !scanSymbols.trim()} sx={{ height: '56px' }}>
                {scanning ? 'Scanning...' : 'Scan Selected'}
              </Button>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Button fullWidth variant="outlined" onClick={handleScanAll}
                disabled={scanning} sx={{ height: '56px' }}>
                {scanning ? 'Scanning...' : 'Scan All 512'}
              </Button>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} />}
              label="Available only"
            />
            <Button startIcon={<Refresh />} onClick={fetchRecommendations} disabled={loading}>
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

      {/* Table */}
      {!loading && recommendations.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Symbol</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell align="right">Entry</TableCell>
                <TableCell align="right">Target</TableCell>
                <TableCell align="right">Stop Loss</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'expected_return_pct'}
                    direction={sortField === 'expected_return_pct' ? sortDir : 'desc'}
                    onClick={() => handleSort('expected_return_pct')}
                  >
                    Expected Return
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'risk_reward_ratio'}
                    direction={sortField === 'risk_reward_ratio' ? sortDir : 'desc'}
                    onClick={() => handleSort('risk_reward_ratio')}
                  >
                    Risk/Reward
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'confidence_score'}
                    direction={sortField === 'confidence_score' ? sortDir : 'desc'}
                    onClick={() => handleSort('confidence_score')}
                  >
                    Confidence
                  </TableSortLabel>
                </TableCell>
                <TableCell>Scanned</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRecs.map((rec) => (
                <React.Fragment key={rec.id}>
                  <TableRow
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: rec.acted_upon ? theme.palette.action.hover : 'inherit',
                      opacity: rec.is_expired ? 0.6 : 1,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    }}
                    onClick={() => toggleExpand(rec.id)}
                  >
                    {/* Expand toggle */}
                    <TableCell sx={{ py: 1, pr: 0 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleExpand(rec.id); }}>
                        {expandedId === rec.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        placement="right"
                        arrow
                        title={(() => {
                          const name = rec.strategy_data?.company_name ?? stockMeta[rec.symbol]?.name ?? rec.symbol;
                          const rawSector = stockMeta[rec.symbol]?.sector ?? rec.strategy_data?.sector ?? '';
                          const sector = /^unknown$/i.test(rawSector.trim()) ? '' : rawSector;
                          return (
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{name}</Typography>
                              {sector && (
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>{sector}</Typography>
                              )}
                            </Box>
                          );
                        })()}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ cursor: 'help', display: 'inline-block' }}>
                          {rec.symbol}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={rec.direction === 'long' ? <TrendingUp /> : <TrendingDown />}
                        label={rec.direction.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: getDirectionColor(rec.direction) + '20',
                          color: getDirectionColor(rec.direction),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2">${rec.current_price.toFixed(2)}</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        ${rec.target_price.toFixed(2)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        ${rec.stop_loss_price.toFixed(2)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Chip
                        label={`${rec.expected_return_pct >= 0 ? '+' : ''}${rec.expected_return_pct.toFixed(1)}%`}
                        size="small"
                        color={rec.expected_return_pct >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {rec.risk_reward_ratio.toFixed(2)}:1
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <CircularProgress
                          variant="determinate"
                          value={rec.confidence_score * 100}
                          size={40}
                          sx={{ color: getConfidenceColor(rec.confidence_score) }}
                        />
                        <Box sx={{
                          top: 0, left: 0, bottom: 0, right: 0,
                          position: 'absolute', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Typography variant="caption" fontWeight={600}>
                            {(rec.confidence_score * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(rec.scanned_at).toLocaleDateString()}
                      </Typography>
                      {rec.is_expired && (
                        <Chip label="Expired" size="small" color="error" sx={{ ml: 1 }} />
                      )}
                    </TableCell>

                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {rec.acted_upon ? (
                        <Chip icon={<CheckCircle />} label="Approved" size="small" color="success" />
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Approve">
                            <IconButton size="small" color="success"
                              onClick={() => handleApprove(rec)} disabled={rec.is_expired}>
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(rec.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Comprehensive insight row */}
                  <TableRow sx={{ '& td': { py: 0 } }}>
                    <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
                      <Collapse in={expandedId === rec.id} timeout="auto" unmountOnExit>
                        <ComprehensiveInsightPanel rec={rec} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && recommendations.length === 0 && (
        <Alert severity="info">
          No recommendations found. Scan symbols or adjust filters.
        </Alert>
      )}

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog.open}
        onClose={() => setApprovalDialog({ open: false, recommendation: null })}
        maxWidth="sm" fullWidth
      >
        <DialogTitle>Approve Trading Recommendation</DialogTitle>
        <DialogContent>
          {approvalDialog.recommendation && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Symbol</Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {approvalDialog.recommendation.symbol}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Direction</Typography>
                  <Typography variant="h6">
                    {approvalDialog.recommendation.direction.toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Entry</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    ${approvalDialog.recommendation.current_price.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Target</Typography>
                  <Typography variant="body1" fontWeight={600} color="success.main">
                    ${approvalDialog.recommendation.target_price.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Stop Loss</Typography>
                  <Typography variant="body1" fontWeight={600} color="error.main">
                    ${approvalDialog.recommendation.stop_loss_price.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Reason</Typography>
                  <Typography variant="body2">
                    {approvalDialog.recommendation.reason}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Quantity (shares)" type="number"
                    value={approvalQuantity}
                    onChange={(e) => setApprovalQuantity(e.target.value)}
                    helperText={`Estimated cost: $${(
                      approvalDialog.recommendation.current_price * parseInt(approvalQuantity || '0')
                    ).toFixed(2)}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Notes (optional)" multiline rows={2}
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog({ open: false, recommendation: null })}>Cancel</Button>
          <Button
            onClick={handleConfirmApproval} variant="contained"
            disabled={approving || !approvalQuantity || parseInt(approvalQuantity) <= 0}
          >
            {approving ? 'Approving...' : 'Approve & Open Position'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Recommendations;
